<template>
  <div class="list">
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>外管工作台</Breadcrumb-item>
        <Breadcrumb-item>出勤风险跟进</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div class="rule-bar">
      <span class="rule-dot"></span>
      跟进口径：当天有 <b>请假单</b>（一条请假记录一条跟进，含无约课）或 <b>旷课</b>（含旷课·失联）课节的老师进入本板块，由外管核实并登记跟进结果；迟到只在类型选「迟到 / 全部」时出现。
    </div>
    <div class="filter-bar">
      <span class="toolbar-label">外教运营</span>
      <adviser-select v-model="adviserId" @on-change="onAdviserChange"></adviser-select>
      <Button-group>
        <Button :type="dateMode === 'yesterday' ? 'primary' : 'ghost'" @click="changeDateMode('yesterday')">
          昨日（{{ yesterdayStr.slice(5) }}）
        </Button>
        <Button :type="dateMode === 'today' ? 'primary' : 'ghost'" @click="changeDateMode('today')">今日</Button>
      </Button-group>
      <Date-picker type="date" :value="customDate" placeholder="自选日期" :clearable="false" transfer
                   class="date-picker" :class="{ 'is-active': dateMode === 'custom' }"
                   @on-change="onCustomDate"></Date-picker>
      <Button-group>
        <Button v-for="item in statusTabs" :key="item.value"
                :type="status === item.value ? 'primary' : 'ghost'"
                @click="changeStatus(item.value)">{{ item.label }}</Button>
      </Button-group>
    </div>
    <div class="filter-bar">
      <Button-group>
        <Button v-for="item in typeTabs" :key="item.value"
                :type="typeKey === item.value ? 'primary' : 'ghost'"
                @click="changeType(item.value)">{{ item.label }}</Button>
      </Button-group>
      <Input v-model="keyword" class="keyword" placeholder="搜索老师姓名 / ID" clearable
             @on-enter="search" @on-clear="search">
        <Button slot="append" icon="search" @click="search"></Button>
      </Input>
      <span class="toolbar-spacer"></span>
      <Button @click="exportCsv">导出</Button>
      <Button @click="openManual">手动登记</Button>
      <Button v-if="pendingList.length" type="primary" @click="openBatch">批量跟进当前 {{ pendingList.length }} 节</Button>
    </div>
    <div class="stat-bar">
      <span class="stat-date">{{ queryDate }}</span>
      <Tag v-for="(item, code) in typeDict" :key="code" :color="item.color">{{ item.text }} {{ typeCounts[code] || 0 }}</Tag>
      <span class="muted">当前 {{ groups.length }} 位老师 · {{ pendingLessons }} 节课待跟进</span>
    </div>
    <Spin v-if="searchLoading" fix></Spin>
    <div v-if="!groups.length && !searchLoading" class="empty-card">
      <div class="empty-big">当日无需跟进的老师</div>
      <div class="muted">当天有请假单、旷课课节的老师会出现在这里，供外管逐人跟进</div>
    </div>
    <div v-else class="follow-list">
    <div v-for="g in groups" :key="g.tchId" class="tch-card">
      <div class="tch-card-head">
        <span class="avatar" :class="g.absentCount ? 'is-danger' : 'is-warning'">{{ initials(g) }}</span>
        <div class="tch-card-name">
          <span class="tch-name-row">
            <a href="javascript:;" @click="openTchInfo(g.tchId)">{{ tchLabel(g) }}</a>
            <span v-if="scoreText(g)" class="tch-score-badge">{{ scoreText(g) }}</span>
          </span>
          <div class="muted">ID {{ g.tchId }}<span v-if="g.country"> · {{ g.country }}</span> · {{ g.studyAdviserName || '未分配' }}</div>
        </div>
        <div class="tch-card-tags">
          <Tag v-if="g.absentCount" color="red">旷课 {{ g.absentCount }} 节</Tag>
          <Tag v-if="g.leaveCount" color="yellow">请假 {{ g.leaveCount }} 条</Tag>
          <Tag v-if="g.lateCount" color="blue">迟到 {{ g.lateCount }} 节</Tag>
          <Tag v-if="g.pendingCount" color="red">{{ g.pendingCount }} 节待跟进</Tag>
          <Tag v-else color="green">已全部跟进</Tag>
        </div>
        <span v-if="g.cleanDays != null" class="clean-days">连续无异常 {{ g.cleanDays }} 天</span>
        <span class="toolbar-spacer"></span>
        <span class="muted">共 {{ g.total || (g.lessons || []).length }} 节</span>
        <Button size="small" @click="openTchInfo(g.tchId)">档案</Button>
      </div>
      <div class="tch-card-body">
        <div v-for="l in g.lessons" :key="lessonRowKey(l)" class="lesson-row">
          <span class="lesson-bar" :class="isAbsentType(l.type) ? 'is-danger' : 'is-warning'"></span>
          <div class="lesson-main">
            <div class="lesson-title">
              <b v-if="!l.manual">{{ l.beginTime || '—' }}</b>
              <Tag :color="typeColor(l.type)">{{ typeText(l.type) }}</Tag>
              <Tag v-if="l.handled" color="green">已跟进</Tag>
              <Tag v-else color="red">待跟进</Tag>
            </div>
            <div v-if="l.manual" class="muted">手动 · 无学生</div>
            <div v-else class="muted">
              <template v-if="l.type === 'LEAVE'">请假单 {{ l.lessonId }} · 原因：{{ l.reason || '未填写' }}</template>
              <template v-else>课节 {{ l.lessonId }} · 学员 {{ l.studentName || l.uid || '—' }} · 原因：{{ l.reason || '未填写' }}</template>
            </div>
            <div v-if="l.handled" class="lesson-handled">
              跟进结果：{{ resultText(l.result) }}<span v-if="l.note"> · {{ l.note }}</span>
              · {{ l.handledBy || '' }} {{ l.handledAt || '' }}
            </div>
          </div>
          <Button size="small" :type="l.handled ? 'ghost' : 'primary'" @click="openSingle(g, l)">
            {{ l.handled ? '改判' : '跟进' }}
          </Button>
        </div>
      </div>
    </div>
    </div>
    <followup-handle-modal :value="showHandle" :lessons="handleLessons"
                           @on-close="showHandle = false" @on-success="search"></followup-handle-modal>
    <followup-manual-modal :value="showManual" :default-date="queryDate"
                           @on-close="showManual = false" @on-success="search"></followup-manual-modal>
    <tch-info-modal :value="showTchInfoModal" :tchId="tchInfoId" @on-close="showTchInfoModal = false"></tch-info-modal>
  </div>
</template>
<script>
import AdviserSelect from './AdviserSelect.vue'
import FollowupHandleModal from './modal/FollowupHandleModal.vue'
import FollowupManualModal from './modal/FollowupManualModal.vue'
import TchInfoModal from '../teacher/modal/TchInfoModal.vue'
import { API_PREFIX, FOLLOWUP_TYPE, FOLLOWUP_RESULT, codeText, codeColor, tchLabel, adviserParam, downloadCsv, resolveAdviserId, writeStoredAdviserId} from './constants'
import { fetchTchScoreMap, applyTchScoresToRows } from '@/utils/tchScore'
const TYPE_TABS = [
  { value: 'main', label: '请假+旷课', types: ['LEAVE', 'ABSENT', 'LOST'] },
  { value: 'leave', label: '请假', types: ['LEAVE'] },
  { value: 'absent', label: '旷课', types: ['ABSENT', 'LOST'] },
  { value: 'late', label: '迟到', types: ['LATE'] },
  { value: 'all', label: '全部类型', types: ['LEAVE', 'ABSENT', 'LOST', 'LATE'] }
]
const STATUS_KEYS = ['pending', 'done', 'all']
export default {
  name: 'HrWorkbenchFollowup',
  components: { AdviserSelect, FollowupHandleModal, FollowupManualModal, TchInfoModal },
  data () {
    let q = this.$route.query || {}
    let dateMode = 'yesterday'
    let customDate = ''
    if (q.date === 'today') {
      dateMode = 'today'
    } else if (q.date && /^\d{4}-\d{2}-\d{2}$/.test(q.date)) {
      dateMode = 'custom'
      customDate = q.date
    }
    return {
      adviserId: resolveAdviserId(q),
      dateMode: dateMode,
      customDate: customDate,
      status: STATUS_KEYS.indexOf(q.status) >= 0 ? q.status : 'pending',
      typeKey: 'main',
      keyword: q.keyword || '',
      statusTabs: [
        { value: 'pending', label: '待跟进' },
        { value: 'done', label: '已跟进' },
        { value: 'all', label: '全部' }
      ],
      typeTabs: TYPE_TABS,
      typeDict: FOLLOWUP_TYPE,
      yesterdayStr: this.$moment().subtract(1, 'days').format('YYYY-MM-DD'),
      todayStr: this.$moment().format('YYYY-MM-DD'),
      searchLoading: false,
      typeCounts: {},
      pendingLessons: 0,
      groups: [],
      rankScoreSeq: 0,
      showHandle: false,
      handleLessons: [],
      showManual: false,
      showTchInfoModal: false,
      tchInfoId: null
    }
  },
  computed: {
    queryDate () {
      if (this.dateMode === 'today') {
        return this.todayStr
      }
      if (this.dateMode === 'custom' && this.customDate) {
        return this.customDate
      }
      return this.yesterdayStr
    },
    queryTypes () {
      let key = this.typeKey
      let tab = TYPE_TABS.filter(function (t) {
        return t.value === key
      })[0] || TYPE_TABS[0]
      return tab.types
    },
    pendingList () {
      let list = []
      let self = this
      this.groups.forEach(function (g) {
        (g.lessons || []).forEach(function (l) {
          if (!l.handled) {
            list.push(self.toHandleItem(g, l))
          }
        })
      })
      return list
    }
  },
  mounted () {
    writeStoredAdviserId(this.adviserId)
    this.$root.$emit('hr-badges-refresh')

    this.search()
  },
  methods: {
    tchLabel (row) {
      return tchLabel(row)
    },
    initials (g) {
      let name = g.tchName || String(g.tchId || '?')
      return name.split(/\s+/).map(function (w) {
        return w.charAt(0)
      }).slice(0, 2).join('').toUpperCase()
    },
    isAbsentType (type) {
      return type === 'ABSENT' || type === 'LOST'
    },
    typeText (code) {
      return codeText(FOLLOWUP_TYPE, code)
    },
    typeColor (code) {
      return codeColor(FOLLOWUP_TYPE, code)
    },
    resultText (code) {
      return codeText(FOLLOWUP_RESULT, code) || '—'
    },
    changeDateMode (mode) {
      this.dateMode = mode
      this.search()
    },
    onCustomDate (val) {
      if (!val) {
        return
      }
      this.customDate = val
      this.dateMode = 'custom'
      this.search()
    },
    changeStatus (status) {
      this.status = status
      this.search()
    },
    changeType (key) {
      this.typeKey = key
      this.search()
    },
    onAdviserChange () {
      writeStoredAdviserId(this.adviserId)
      this.$root.$emit('hr-badges-refresh')
      this.search()
    },
    search () {
      let self = this
      let params = Object.assign({
        date: self.queryDate,
        types: self.queryTypes.join(','),
        status: self.status
      }, adviserParam(self.adviserId))
      if (self.keyword && self.keyword.trim()) {
        params.keyword = self.keyword.trim()
      }
      self.searchLoading = true
      self.$http.post(API_PREFIX + '/followup/list', params).then(function (res) {
        if (res && res.status === 'ok' && res.data) {
          self.typeCounts = res.data.typeCounts || {}
          self.pendingLessons = res.data.pendingLessons || 0
          self.groups = res.data.groups || []
          self.loadMonthRankScores()
        } else {
          self.typeCounts = {}
          self.pendingLessons = 0
          self.groups = []
        }
      }).finally(function () {
        self.searchLoading = false
      })
    },
    scoreText (g) {
      if (!g || g.monthRankScore == null || g.monthRankScore === '') {
        return ''
      }
      const num = Number(g.monthRankScore)
      if (isNaN(num)) {
        return ''
      }
      return num.toFixed(2)
    },
    loadMonthRankScores () {
      const rows = this.groups || []
      if (!rows.length) {
        return
      }
      const ids = rows.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, rows, 'tchId', 'monthRankScore', map)
      })
    },
    toHandleItem (g, l) {
      return Object.assign({}, l, {
        tchId: g.tchId,
        tchName: g.tchName,
        lessonDate: l.lessonDate || this.queryDate
      })
    },
    openSingle (g, l) {
      this.handleLessons = [this.toHandleItem(g, l)]
      this.showHandle = true
    },
    openBatch () {
      if (!this.pendingList.length) {
        this.$Message.info('当前范围内没有待跟进课节')
        return
      }
      this.handleLessons = this.pendingList.slice()
      this.showHandle = true
    },
    openTchInfo (tchId) {
      this.tchInfoId = tchId
      this.showTchInfoModal = true
    },
    lessonRowKey (l) {
      if (l.manualId != null) {
        return 'manual-' + l.manualId
      }
      return String(l.lessonId) + '-' + String(l.type || '')
    },
    openManual () {
      this.showManual = true
    },
    exportCsv () {
      let self = this
      let rows = []
      this.groups.forEach(function (g) {
        (g.lessons || []).forEach(function (l) {
          rows.push([
            tchLabel(g),
            g.tchId,
            g.country || '',
            g.studyAdviserName || '未分配',
            g.cleanDays == null ? '' : g.cleanDays,
            l.manual ? '' : (l.beginTime || ''),
            self.typeText(l.type),
            l.handled ? '已跟进' : '待跟进',
            l.manual ? '手动' : (l.lessonId == null ? '' : l.lessonId),
            (l.manual || l.type === 'LEAVE') ? (l.type === 'LEAVE' ? '—' : '无学生') : (l.studentName || l.uid || ''),
            l.manual ? '' : (l.reason || ''),
            l.handled ? self.resultText(l.result) : '',
            l.note || '',
            l.handledBy || '',
            l.handledAt || '',
            l.handled ? '改判' : '跟进'
          ])
        })
      })
      if (!rows.length) {
        this.$Message.warning('没有可导出的数据')
        return
      }
      downloadCsv('出勤风险跟进-' + this.queryDate + '.csv', ['外教', '老师ID', '国家', '外教运营', '连续无异常天数', '时间', '类型', '跟进状态', '课节/请假单', '学员', '原因', '跟进结果', '跟进备注', '跟进人', '跟进时间', '操作'], rows)
    }
  }
}
</script>
<style scoped lang="less">
@danger: #E6464E;
@warning: #E89F2D;

.list {
  position: relative;
}
.rule-bar {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #f8f8f9;
  border-radius: 4px;
  line-height: 22px;
  font-size: 12px;
  color: #495060;
}
.rule-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
  background: @danger;
}
.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.toolbar-label {
  color: #495060;
}
.toolbar-spacer {
  flex: 1;
}
.date-picker {
  width: 140px;
}
.date-picker.is-active /deep/ .ivu-input {
  border-color: #2d8cf0;
  color: #2d8cf0;
}
.keyword {
  width: 240px;
}
.muted {
  color: #80848f;
  font-size: 12px;
}
.stat-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid #e9eaec;
  border-radius: 4px;
}
.stat-date {
  margin-right: 8px;
  font-weight: 600;
  color: #495060;
}
.empty-card {
  padding: 40px 0;
  text-align: center;
  border: 1px dashed #dddee1;
  border-radius: 4px;
}
.empty-big {
  margin-bottom: 6px;
  font-size: 15px;
  color: #495060;
}
.tch-card {
  margin-bottom: 10px;
  border: 1px solid #e9eaec;
  border-radius: 4px;
  background: #fff;
}
.tch-card-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafbfc;
}
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  &.is-danger {
    background: @danger;
  }
  &.is-warning {
    background: @warning;
  }
}
.tch-card-name a {
  font-weight: 600;
}
.tch-card-body {
  padding: 2px 16px 6px;
}
.lesson-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #e9eaec;
  &:last-child {
    border-bottom: 0;
  }
}
.lesson-bar {
  align-self: stretch;
  width: 3px;
  border-radius: 2px;
  &.is-danger {
    background: @danger;
  }
  &.is-warning {
    background: @warning;
  }
}
.lesson-main {
  flex: 1;
  min-width: 0;
}
.lesson-title b {
  margin-right: 6px;
}
.lesson-handled {
  margin-top: 2px;
  font-size: 12px;
  color: #0B9E8D;
}
.clean-days {
  color: #0B9E8D;
  font-size: 12px;
  font-weight: 600;
}

.tch-name-row {
  display: inline-flex;
  align-items: flex-start;
  white-space: nowrap;
}
.tch-score-badge {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 7px;
  background: #ff9900;
  color: #fff;
  font-size: 10px;
  line-height: 14px;
}
</style>



// WEBPACK FOOTER //
// HrWorkbenchFollowup.vue?4eb5d844